import React from 'react';
import dayjs from 'dayjs';
import { Flex, MultiSelect as MantineMultiSelect, Tooltip } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { IoIosArrowDown } from 'react-icons/io';
import Input from '../../../../../../../components/Input';
import ErrorBoundary from '../../../../../../../components/ErrorBoundary';
import { FieldLabel, OptionalLabel, FieldGroup, CompactFields, OptionLabel } from './style';

const toLocalDate = value => {
  if (!value) return null;
  if (value instanceof Date) return value;
  return dayjs(value, 'YYYY-MM-DD').toDate(); // local
};

const MANAGER_SECTION_OPTIONS = [
  {
    value: '1a',
    label: '1a',
    disabled: true,
    tooltip: 'Section 1a — Basic manager information. Required and cannot be deselected.'
  },
  {
    value: '2a',
    label: '2a',
    tooltip:
      'You are required to complete Section 2a if you and your related persons, collectively, had at least $1.5 billion in hedge fund assets under management as of the last day of any month in the fiscal quarter immediately preceding your most recently completed fiscal quarter. You are not required to include the regulatory assets under management of any related person that is separately operated.'
  }
];

export const FormPFManagerInfo = ({
  sec_number,
  nfa_id,
  lei_id,
  large_trader_id,
  large_trader_id_suffix,
  fiscal_year_end,
  manager_sections,
  handleManagerInputChange,
  handleManagerFiscalYearChange,
  projectName
}) => {
  const handleManagerSectionsChange = selected => {
    // 1a is mandatory — always keep it selected
    const withMandatory = selected.includes('1a') ? selected : ['1a', ...selected];
    handleManagerInputChange('manager_sections', withMandatory);
  };

  return (
    <CompactFields>
      <Flex direction="column" gap={8} w={260} pr={16}>
        <FieldGroup>
          <FieldLabel>Manager</FieldLabel>
          <Input
            value={projectName}
            inputId="input-manager-name"
            placeholder="Manager"
            boxClass="compact-field"
            disabled
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Sections</FieldLabel>
          <MantineMultiSelect
            w="100%"
            placeholder="Select"
            rightSection={<IoIosArrowDown />}
            data={MANAGER_SECTION_OPTIONS}
            value={manager_sections || ['1a']}
            onChange={handleManagerSectionsChange}
            renderOption={({ option }) => (
              <Tooltip
                label={option.tooltip}
                disabled={!option.tooltip}
                position="right"
                withArrow
                multiline
                w={320}
              >
                <OptionLabel>{option.label}</OptionLabel>
              </Tooltip>
            )}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>SEC ID *</FieldLabel>
          <Input
            required
            value={sec_number}
            inputId="input-manager-sec-id"
            onChange={e => handleManagerInputChange('sec_number', e.target.value)}
            placeholder="SEC ID"
            boxClass="compact-field"
            minLength={14}
            maxLength={14}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Fiscal Year End *</FieldLabel>
          <ErrorBoundary componentName="FormPFManagerInfo">
            <MonthPickerInput
              placeholder="Pick date"
              value={fiscal_year_end ? toLocalDate(fiscal_year_end) : null}
              onChange={dateString => handleManagerFiscalYearChange(dateString)}
            />
          </ErrorBoundary>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>
            NFA ID <OptionalLabel>optional</OptionalLabel>
          </FieldLabel>
          <Input
            value={nfa_id}
            inputId="input-manager-nfa-id"
            onChange={e => handleManagerInputChange('nfa_id', e.target.value)}
            placeholder="NFA ID"
            boxClass="compact-field"
            minLength={7}
            maxLength={7}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>
            LEI ID <OptionalLabel>optional</OptionalLabel>
          </FieldLabel>
          <Input
            value={lei_id}
            inputId="input-manager-lei-id"
            onChange={e => handleManagerInputChange('lei_id', e.target.value)}
            placeholder="LEI ID"
            boxClass="compact-field"
            minLength={20}
            maxLength={20}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Large Trader ID</FieldLabel>
          <Input
            value={large_trader_id}
            inputId="input-manager-lt-id"
            onChange={e => handleManagerInputChange('large_trader_id', e.target.value)}
            placeholder="LT ID"
            boxClass="compact-field"
            minLength={1}
            maxLength={15}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Large Trader ID suffix</FieldLabel>
          <Input
            value={large_trader_id_suffix}
            inputId="input-manager-lt-id-suffix"
            onChange={e => handleManagerInputChange('large_trader_id_suffix', e.target.value)}
            placeholder="LT ID suffix"
            boxClass="compact-field"
            minLength={1}
            maxLength={15}
          />
        </FieldGroup>
      </Flex>
    </CompactFields>
  );
};
